import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Inventory } from "../../../../api";
import { usePermissions } from "../../../../hooks";
import { ACTIONS } from "../../../../utils/constants";

import styles from "./InventoryPage.module.scss";
import {
  LIMIT_OPTIONS,
  flattenExistences,
  formatCurrency,
  formatNumber,
  getExistencesResponse,
  getSummaryResponse,
} from "./InventoryPage.form";

export function InventoryPage() {
  const { hasAction } = usePermissions();
  // Entrada, Salida y Ajuste son operaciones distintas entre sí (no una
  // genérica "agregar registro"), así que cada una tiene su propio
  // permiso -- alguien puede tener permiso para registrar entradas sin
  // poder hacer ajustes de inventario, por ejemplo.
  const canEntry = hasAction("inventory", ACTIONS.ENTRY);
  const canExit = hasAction("inventory", ACTIONS.EXIT);
  const canAdjust = hasAction("inventory", ACTIONS.ADJUST);
  const canRegisterMovement = canEntry || canExit || canAdjust;

  const [products, setProducts] = useState([]);

  const [summary, setSummary] = useState({
    activeSkus: 0,
    totalStock: 0,
    lowStockVariants: 0,
    outOfStockVariants: 0,
    inventoryValue: 0,
  });

  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  });

  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [stockFilter, setStockFilter] = useState("");
  const [appliedStockFilter, setAppliedStockFilter] = useState("");

  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadInventory = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const inventoryController = new Inventory();

      const [summaryResult, existencesResult] = await Promise.all([
        inventoryController.getSummary(),
        inventoryController.getExistences(currentPage, limit, {
          search: appliedSearch || undefined,
          stock: appliedStockFilter || undefined,
        }),
      ]);

      const summaryResponse = getSummaryResponse(summaryResult);
      const existencesResponse = getExistencesResponse(existencesResult);

      setSummary({
        activeSkus: Number(summaryResponse.activeSkus) || 0,
        totalStock: Number(summaryResponse.totalStock) || 0,
        lowStockVariants:
          Number(summaryResponse.lowStockVariants) || 0,
        outOfStockVariants:
          Number(summaryResponse.outOfStockVariants) || 0,
        inventoryValue: Number(summaryResponse.inventoryValue) || 0,
      });

      setProducts(existencesResponse.docs || []);

      setPagination({
        totalDocs: Number(existencesResponse.totalDocs) || 0,
        totalPages: Math.max(
          Number(existencesResponse.totalPages) || 1,
          1
        ),
        page: Number(existencesResponse.page) || currentPage,
        limit: Number(existencesResponse.limit) || limit,
      });
    } catch (errorResponse) {
      console.error("loadInventory error:", errorResponse);

      setProducts([]);

      setSummary({
        activeSkus: 0,
        totalStock: 0,
        lowStockVariants: 0,
        outOfStockVariants: 0,
        inventoryValue: 0,
      });

      setPagination({
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit,
      });

      setError(
        errorResponse?.msg ||
          "No fue posible cargar las existencias. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearch, appliedStockFilter, currentPage, limit]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const existences = useMemo(() => {
    return flattenExistences(products);
  }, [products]);

  const firstVisibleItem = useMemo(() => {
    if (pagination.totalDocs === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }, [pagination]);

  const lastVisibleItem = useMemo(() => {
    return Math.min(
      pagination.page * pagination.limit,
      pagination.totalDocs
    );
  }, [pagination]);

  const hasFilters = Boolean(appliedSearch || appliedStockFilter);

  function handleSearchSubmit(event) {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedSearch(searchValue.trim());
  }

  function handleStockFilterChange(event) {
    const filter = event.target.value;

    setStockFilter(filter);
    setAppliedStockFilter(filter);
    setCurrentPage(1);
  }

  function handleClearFilters() {
    setSearchValue("");
    setAppliedSearch("");
    setStockFilter("");
    setAppliedStockFilter("");
    setCurrentPage(1);
  }

  function handleLimitChange(event) {
    setLimit(Number(event.target.value));
    setCurrentPage(1);
  }

  function getStatusClassName(status) {
    if (status === "out") {
      return styles.OutOfStockBadge;
    }

    if (status === "low") {
      return styles.LowStockBadge;
    }

    return styles.OptimalStockBadge;
  }

  return (
    <section className={styles.InventoryPage}>
      <header className={styles.PageHeader}>
        <div>
          <p className={styles.Breadcrumb}>Inventario / Existencias</p>

          <h1>Existencias</h1>

          <p className={styles.Subtitle}>
            Consulta y monitorea el stock disponible de todos los productos.
          </p>
        </div>

        <div className={styles.HeaderActions}>
          {canEntry && (
            <Link
              to="/admin/inventory/entry"
              className={styles.EntryButton}
            >
              <span>＋</span>
              Entrada
            </Link>
          )}

          {canExit && (
            <Link
              to="/admin/inventory/exit"
              className={styles.ExitButton}
            >
              <span>−</span>
              Salida
            </Link>
          )}

          {canAdjust && (
            <Link
              to="/admin/inventory/adjustment"
              className={styles.AdjustmentButton}
            >
              <span>±</span>
              Ajuste
            </Link>
          )}

          <Link
            to="/admin/inventory/movements"
            className={styles.MovementsButton}
          >
            Kardex
          </Link>
        </div>
      </header>

      <div className={styles.SummaryGrid}>
        <article className={styles.SummaryCard}>
          <span>SKU activos</span>
          <strong>{formatNumber(summary.activeSkus)}</strong>
        </article>

        <article className={styles.SummaryCard}>
          <span>Unidades disponibles</span>
          <strong>{formatNumber(summary.totalStock)}</strong>
        </article>

        <article className={styles.SummaryCard}>
          <span>Valor de inventario</span>
          <strong>{formatCurrency(summary.inventoryValue)}</strong>
        </article>

        <article
          className={`${styles.SummaryCard} ${styles.LowStockSummaryCard}`}
        >
          <span>Productos bajo mínimo</span>
          <strong>{formatNumber(summary.lowStockVariants)}</strong>
        </article>

        <article
          className={`${styles.SummaryCard} ${styles.OutOfStockSummaryCard}`}
        >
          <span>Sin stock</span>
          <strong>{formatNumber(summary.outOfStockVariants)}</strong>
        </article>
      </div>

      <section className={styles.InventoryCard}>
        <div className={styles.FiltersBar}>
          <form
            className={styles.SearchForm}
            onSubmit={handleSearchSubmit}
          >
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Buscar producto, SKU o código..."
              aria-label="Buscar existencias"
            />

            <button type="submit" aria-label="Buscar">
              ⌕
            </button>
          </form>

          <label className={styles.FilterControl}>
            <span>Estado de stock</span>

            <select
              value={stockFilter}
              onChange={handleStockFilterChange}
            >
              <option value="">Todos</option>
              <option value="available">Disponible</option>
              <option value="low">Bajo stock</option>
              <option value="out">Sin stock</option>
            </select>
          </label>

          <button
            type="button"
            className={styles.RefreshButton}
            onClick={loadInventory}
            disabled={isLoading}
          >
            {isLoading ? "Actualizando..." : "Actualizar"}
          </button>

          {hasFilters && (
            <button
              type="button"
              className={styles.ClearFiltersButton}
              onClick={handleClearFilters}
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {error && (
          <div className={styles.ErrorMessage} role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className={styles.StateMessage}>
            Cargando existencias...
          </div>
        ) : existences.length === 0 ? (
          <div className={styles.EmptyState}>
            <p>
              {hasFilters
                ? "No se encontraron variantes con los filtros seleccionados."
                : "No hay existencias para mostrar."}
            </p>

            {hasFilters && (
              <button
                type="button"
                className={styles.ClearFiltersButton}
                onClick={handleClearFilters}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.TableContainer}>
              <table className={styles.Table}>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>SKU</th>
                    <th>Variante</th>
                    <th>Disponible</th>
                    <th>Mínimo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {existences.map((existence) => (
                    <tr
                      key={`${existence.productId}-${existence.variantId}`}
                    >
                      <td>
                        <div className={styles.ProductCell}>
                          {existence.productImage ? (
                            <img
                              className={styles.ProductImage}
                              src={existence.productImage}
                              alt=""
                            />
                          ) : (
                            <span className={styles.ProductImagePlaceholder}>
                              ▣
                            </span>
                          )}

                          <div>
                            <strong>{existence.productName}</strong>

                            <span>
                              {existence.productCode || "Sin código"}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={styles.SkuCode}>
                          {existence.sku}
                        </span>
                      </td>

                      <td>
                        <span className={styles.VariantLabel}>
                          {existence.variantLabel}
                        </span>
                      </td>

                      <td>
                        <strong className={styles.StockValue}>
                          {formatNumber(existence.availableStock)}
                        </strong>
                      </td>

                      <td>
                        <span className={styles.MinimumValue}>
                          {formatNumber(existence.lowStockAlert)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={getStatusClassName(
                            existence.stockStatus.status
                          )}
                        >
                          {existence.stockStatus.label}
                        </span>
                      </td>

                      <td>
                        {canRegisterMovement && (
                          <div className={styles.RowActions}>
                            {canEntry && (
                              <Link
                                to="/admin/inventory/entry"
                                state={{
                                  productId: existence.productId,
                                  variantId: existence.variantId,
                                }}
                              >
                                Entrada
                              </Link>
                            )}

                            {canExit && (
                              <Link
                                to="/admin/inventory/exit"
                                state={{
                                  productId: existence.productId,
                                  variantId: existence.variantId,
                                }}
                              >
                                Salida
                              </Link>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <footer className={styles.Pagination}>
              <span className={styles.TableInfo}>
                Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
                {pagination.totalDocs} productos
              </span>

              <div className={styles.PageControls}>
                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(page - 1, 1))
                  }
                  disabled={currentPage === 1}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <span className={styles.CurrentPage}>
                  {pagination.page} de {pagination.totalPages}
                </span>

                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) =>
                      Math.min(page + 1, pagination.totalPages)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <label className={styles.LimitControl}>
                <select value={limit} onChange={handleLimitChange}>
                  {LIMIT_OPTIONS.map((optionLimit) => (
                    <option key={optionLimit} value={optionLimit}>
                      {optionLimit} por página
                    </option>
                  ))}
                </select>
              </label>
            </footer>
          </>
        )}
      </section>
    </section>
  );
}